import { TimeZoneDateFnsService } from 'app/core/services/time-zone-date-fns.service';
import { SpreadsheetDashboardTypeHelper } from 'app/shared/enum-helpers/spreadsheet-dashboard-type.enum.helper';
import { NotificationStatus } from 'app/shared/enums/notification-status.enum';
import { SpreadsheetDashboardType } from 'app/shared/enums/spreadsheet-dashboard-type.enum';
import { CustomDatePipe } from 'app/shared/pipes/custom-date.pipe';
import { ReadSpreadsheetDashboardNotificationViewModel } from 'app/shared/prothesis-api-view-models/read-spreadsheet-dashboard-notification.view-model';
import { buildNotificationCardTitle } from '../../build-notification-card-title';
import { SpreadsheetDashboardNotificationCardModel } from './spreadsheet-dashboard-notification-card.model';

export class SpreadsheetDashboardNotificationCardModelHelper {
  public static build(): SpreadsheetDashboardNotificationCardModel {
    const spreadsheetDashboardNotificationCard = new SpreadsheetDashboardNotificationCardModel();
    return spreadsheetDashboardNotificationCard;
  }

  public static fromViewModel(
    readSpreadsheetDashboardNotificationViewModels: ReadSpreadsheetDashboardNotificationViewModel[],
    customDatePipe: CustomDatePipe,
  ): Array<SpreadsheetDashboardNotificationCardModel> {
    const spreadsheetDashboardNotificationCards = new Array<SpreadsheetDashboardNotificationCardModel>();
    readSpreadsheetDashboardNotificationViewModels.forEach((readSpreadsheetDashboardNotificationViewModel) => {
      const spreadsheetDashboardNotificationCard = this._createModel(readSpreadsheetDashboardNotificationViewModel, customDatePipe);
      spreadsheetDashboardNotificationCards.push(spreadsheetDashboardNotificationCard);
    });

    return spreadsheetDashboardNotificationCards;
  }

  public static fromViewModelSingle(
    readSpreadsheetDashboardNotificationViewModel: ReadSpreadsheetDashboardNotificationViewModel,
    customDatePipe: CustomDatePipe,
  ): SpreadsheetDashboardNotificationCardModel {
    return this._createModel(readSpreadsheetDashboardNotificationViewModel, customDatePipe);
  }

  private static _createModel(
    readSpreadsheetDashboardNotificationViewModel: ReadSpreadsheetDashboardNotificationViewModel,
    customDatePipe: CustomDatePipe,
  ): SpreadsheetDashboardNotificationCardModel {
    const spreadsheetDashboardNotificationCard = new SpreadsheetDashboardNotificationCardModel();
    spreadsheetDashboardNotificationCard.colorClass = 'notification-green';
    spreadsheetDashboardNotificationCard.discriminator = readSpreadsheetDashboardNotificationViewModel.discriminator;
    spreadsheetDashboardNotificationCard.notificationDate = TimeZoneDateFnsService.toLaboratoryTimeZone(
      readSpreadsheetDashboardNotificationViewModel.notificationDate,
    );
    spreadsheetDashboardNotificationCard.readDate = TimeZoneDateFnsService.toLaboratoryTimeZone(
      readSpreadsheetDashboardNotificationViewModel.readDate,
    );
    spreadsheetDashboardNotificationCard.title = buildNotificationCardTitle(
      'Excel',
      readSpreadsheetDashboardNotificationViewModel.notificationDate,
      customDatePipe,
    );
    spreadsheetDashboardNotificationCard.messageLine1 = this.buildMessageLine1(readSpreadsheetDashboardNotificationViewModel);
    spreadsheetDashboardNotificationCard.statusFailedFlag =
      readSpreadsheetDashboardNotificationViewModel.status === NotificationStatus.Failed;
    spreadsheetDashboardNotificationCard.statusNothingToDoFlag =
      readSpreadsheetDashboardNotificationViewModel.status === NotificationStatus.NothingToDo;
    spreadsheetDashboardNotificationCard.statusPendingFlag =
      readSpreadsheetDashboardNotificationViewModel.status === NotificationStatus.Pending;
    spreadsheetDashboardNotificationCard.statusSucceedFlag =
      readSpreadsheetDashboardNotificationViewModel.status === NotificationStatus.Succeed;
    spreadsheetDashboardNotificationCard.uuid = readSpreadsheetDashboardNotificationViewModel.uuid;
    return spreadsheetDashboardNotificationCard;
  }

  private static buildMessageLine1(
    readSpreadsheetDashboardNotificationViewModel: ReadSpreadsheetDashboardNotificationViewModel,
  ): string | undefined {
    if (readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails.length > 0) {
      if (readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails.length === 1) {
        const spreadsheetDashboardNotificationDetail =
          readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails[0];
        return 'Excel - ' + SpreadsheetDashboardTypeHelper.translateEnum(spreadsheetDashboardNotificationDetail.spreadsheetDashboardType);
      } else {
        if (
          readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails.every(
            (spreadsheetDashboardNotificationDetail) =>
              [
                SpreadsheetDashboardType.InvoicesByCustomerAmount,
                SpreadsheetDashboardType.InvoicesByCustomerPercentage,
                SpreadsheetDashboardType.InvoicesByCustomerDiscountAmount,
                SpreadsheetDashboardType.InvoicesByCustomerDeliveryFormDiscountAmount,
              ].includes(spreadsheetDashboardNotificationDetail.spreadsheetDashboardType),
          )
        ) {
          return 'Excel multiples - Statistiques clients';
        } else if (
          readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails.every(
            (spreadsheetDashboardNotificationDetail) =>
              [
                SpreadsheetDashboardType.InvoicesByFamilyAmount,
                SpreadsheetDashboardType.InvoicesByFamilyPercentage,
                SpreadsheetDashboardType.InvoicesByFamilyQuantity,
                SpreadsheetDashboardType.InvoicesByFamilyDeliveryFormDiscountAmount,
                SpreadsheetDashboardType.InvoicesByFamilyDeliveryFormDiscountQuantity,
              ].includes(spreadsheetDashboardNotificationDetail.spreadsheetDashboardType),
          )
        ) {
          return 'Excel multiples - Statistiques familles';
        } else if (
          readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails.every(
            (spreadsheetExportNotificationDetail) =>
              [
                SpreadsheetDashboardType.InvoicesByItemAmount,
                SpreadsheetDashboardType.InvoicesByItemPercentage,
                SpreadsheetDashboardType.InvoicesByItemQuantity,
                SpreadsheetDashboardType.InvoicesByItemDeliveryFormDiscountAmount,
                SpreadsheetDashboardType.InvoicesByItemDeliveryFormDiscountQuantity,
              ].includes(spreadsheetExportNotificationDetail.spreadsheetDashboardType),
          )
        ) {
          return 'Excel multiples - Statistiques articles';
        } else if (
          readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails.every(
            (spreadsheetDashboardNotificationDetail) =>
              [SpreadsheetDashboardType.InvoicesByYear].includes(spreadsheetDashboardNotificationDetail.spreadsheetDashboardType),
          )
        ) {
          return 'Excel multiples - Statistiques par années';
        } else if (
          readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails.every(
            (spreadsheetDashboardNotificationDetail) =>
              [
                SpreadsheetDashboardType.ActivityByItemQuantityByLaboratoryResource,
                SpreadsheetDashboardType.ActivityByLaboratoryResourceAmountByMonth,
                SpreadsheetDashboardType.ActivityByLaboratoryResourceDurationByMonth,
                SpreadsheetDashboardType.ActivityBySectorAmountByMonth,
                SpreadsheetDashboardType.ActivityBySectorDurationByMonth,
              ].includes(spreadsheetDashboardNotificationDetail.spreadsheetDashboardType),
          )
        ) {
          return "Excel multiples - Statistiques suivi d'activité";
        } else if (
          readSpreadsheetDashboardNotificationViewModel.spreadsheetDashboardNotificationDetails.every(
            (spreadsheetDashboardNotificationDetail) =>
              [
                SpreadsheetDashboardType.ActivityByItemQuantityByLaboratoryResource,
                SpreadsheetDashboardType.ActivityByLaboratoryResourceAmountByYear,
                SpreadsheetDashboardType.ActivityByLaboratoryResourceDurationByYear,
                SpreadsheetDashboardType.ActivityBySectorAmountByYear,
                SpreadsheetDashboardType.ActivityBySectorDurationByYear,
              ].includes(spreadsheetDashboardNotificationDetail.spreadsheetDashboardType),
          )
        ) {
          return "Excel multiples - Statistiques suivi d'activité";
        } else {
          return 'Excel multiples';
        }
      }
    }
  }
}
